/*
 * FEDERAÇÃO DOS PROFISSIONAIS DA BELEZA
 * FRONTEND - AUTH SERVICE
 *
 * UPDATED: 2026-03-25 — v2.3
 * ✅ FIX: schoolName e logoUrl mapeados no normalizeUser
 *         (backend agora retorna esses campos no login de affiliate)
 */

import { apiClient, TOKEN_KEY, REFRESH_TOKEN_KEY, USER_DATA_KEY } from './api';

// ─── Types públicos ───────────────────────────────────────────────────────────

export interface AuthUser {
  id:           string;
  email:        string;
  name:         string;
  userType:     'student' | 'affiliate' | 'admin';
  role:         'student' | 'affiliate' | 'admin';
  photoUrl?:    string;
  systemId?:    string;
  // Filiado
  affiliateId?: string;
  schoolName?:  string;   // ✅ NOVO
  logoUrl?:     string;   // ✅ NOVO
  // Aluno
  studentId?:   string;
  status?:      'active' | 'pending' | 'inactive' | 'suspended';
  linkedAffiliateId?: string;
  promotedAs?: 'professional' | 'affiliate';
  generatedId?: string;
  registroFepcssp?: string;
}

export interface LoginRequest {
  email:     string;
  password:  string;
  userType?: 'student' | 'affiliate' | 'admin';
}

export interface LoginResponse {
  accessToken:  string;
  refreshToken: string;
  expiresIn:    number;
  user: {
    id:          string;
    email:       string;
    userType:    'student' | 'affiliate' | 'admin';
    systemId:    string;
    fullName?:   string;
    // Filiado
    affiliateId?: string | null;
    schoolName?:  string | null;
    logoUrl?:     string | null;
    status?:      string | null;
    // Aluno
    studentId?:   string | null;
  };
}

export interface RefreshTokenResponse {
  accessToken: string;
  expiresIn:   number;
}

// ─── Tipo interno do normalizeUser ────────────────────────────────────────────
type RawUser = {
  id?:          unknown;
  email?:       unknown;
  name?:        unknown;
  nome?:        unknown;
  fullName?:    unknown;
  userType?:    unknown;
  tipo?:        unknown;
  role?:        unknown;
  photoUrl?:    unknown;
  foto?:        unknown;
  avatarUrl?:   unknown;
  systemId?:    unknown;
  // Filiado
  affiliateId?: unknown;
  schoolName?:  unknown;   // ✅ NOVO
  logoUrl?:     unknown;   // ✅ NOVO
  // Aluno
  studentId?:   unknown;
  status?:      unknown;
  linkedAffiliateId?: unknown;
  promotedAs?: unknown;
  generatedId?: unknown;
  registroFepcssp?: unknown;
};

// ─── AuthService ──────────────────────────────────────────────────────────────

class AuthService {

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
    this.saveSession(response);
    return response;
  }

  async getMe(): Promise<AuthUser> {
    const raw        = await apiClient.post<RawUser>('/auth/me');
    const normalized = this.normalizeUser(raw);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(normalized));
    return normalized;
  }

  async logout(): Promise<void> {
    const role = this.getCachedUser()?.role;

    // O logout local é imediato, mesmo se o backend estiver acordando ou
    // indisponível. A chamada remota serve apenas para limpar o cookie HTTP.
    void apiClient.post('/auth/logout').catch(() => undefined);
    this.clearSession();

    if (role === 'admin')          window.location.href = '/login-adm';
    else if (role === 'affiliate') window.location.href = '/login/filiado';
    else                           window.location.href = '/login/aluno';
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!storedRefresh) throw new Error('No refresh token available');

    const response = await apiClient.post<RefreshTokenResponse>(
      '/auth/refresh',
      { refreshToken: storedRefresh },
    );
    localStorage.setItem(TOKEN_KEY, response.accessToken);
    return response;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  }

  getCachedUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_DATA_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as AuthUser; }
    catch { return null; }
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  // ─── Privados ───────────────────────────────────────────────────────────────

  private saveSession(response: LoginResponse): void {
    localStorage.setItem(TOKEN_KEY,         response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
    localStorage.setItem(USER_DATA_KEY,     JSON.stringify(
      this.normalizeUser(response.user),
    ));
  }

  private normalizeUser(user: RawUser): AuthUser {
    const userType = this.normalizeUserType(
      String(user.userType ?? user.tipo ?? user.role ?? ''),
    );

    return {
      id:       String(user.id    ?? ''),
      email:    String(user.email ?? ''),
      name:     String(user.name  ?? user.nome ?? user.fullName ?? user.email ?? ''),
      userType,
      role:     userType,
      photoUrl:    user.photoUrl  != null ? String(user.photoUrl)  : undefined,
      systemId:    user.systemId  != null ? String(user.systemId)  : undefined,
      // Filiado
      affiliateId: user.affiliateId != null ? String(user.affiliateId) : undefined,
      schoolName:  user.schoolName  != null ? String(user.schoolName)  : undefined, // ✅
      logoUrl:     user.logoUrl     != null ? String(user.logoUrl)     : undefined, // ✅
      // Aluno
      studentId:   user.studentId   != null ? String(user.studentId)   : undefined,
      status:      user.status as AuthUser['status'],
      linkedAffiliateId: user.linkedAffiliateId != null ? String(user.linkedAffiliateId) : undefined,
      promotedAs: user.promotedAs as AuthUser['promotedAs'],
      generatedId: user.generatedId != null ? String(user.generatedId) : undefined,
      registroFepcssp: user.registroFepcssp != null ? String(user.registroFepcssp) : undefined,
    };
  }

  private normalizeUserType(raw: string): 'student' | 'affiliate' | 'admin' {
    const map: Record<string, 'student' | 'affiliate' | 'admin'> = {
      student:       'student',
      aluno:         'student',
      affiliate:     'affiliate',
      afiliado:      'affiliate',
      filiado:       'affiliate',
      admin:         'admin',
      administrador: 'admin',
    };
    return map[raw.toLowerCase()] ?? 'student';
  }

  private clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  }
}

export const authService = new AuthService();
export default authService;
